import { useEffect, useState } from 'react';
import { Box, ListSubheader, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { NODEBOT_CUSTOM_CHATBOTS, NODEBOT_NODEBOTS } from '../../../config/urls';
import { useCtwaApi } from './api';
import { AUTOFILL_MAX } from './constants';
import { FieldLabel, Note } from './ui';

// "All ads" or one value per ad. Autofill and chatbot each have their own switch.
const AppliesTo = ({ label, mode, onChange }) => (
  <TextField select size="small" label={label} value={mode} onChange={(e) => onChange(e.target.value)} sx={{ width: 220 }}>
    <MenuItem value="all">All ads</MenuItem>
    <MenuItem value="per_ad">Set per ad</MenuItem>
  </TextField>
);

const autofillField = (value, onChange, label) => (
  <Box>
    <FieldLabel>{label}</FieldLabel>
    <TextField fullWidth size="small" value={value} placeholder="e.g. Hi, I want to know about the offer"
      onChange={(e) => onChange(e.target.value.slice(0, AUTOFILL_MAX))} />
    <Note sx={{ textAlign: 'right' }}>{value.length} / {AUTOFILL_MAX}</Note>
  </Box>
);

// Active message-type workflows (nodebots) and active custom chatbots, as dropdown options.
// Option values are "nodebot:<id>" / "custom_chatbot:<id>" — the form's chatbot value.
export const useChatbots = () => {
  const api = useCtwaApi();
  const [bots, setBots] = useState({ nodebots: [], custom: [], loaded: false });
  useEffect(() => {
    Promise.allSettled([api.get(`${NODEBOT_NODEBOTS}?limit=200`), api.get(`${NODEBOT_CUSTOM_CHATBOTS}?limit=200`)])
      .then(([nb, cb]) => setBots({
        nodebots: nb.status === 'fulfilled' ? (nb.value.results || []).filter((b) => b.is_active && b.trigger_type !== 'recurring') : [],
        custom: cb.status === 'fulfilled' ? (cb.value.results || []).filter((b) => b.is_active) : [],
        loaded: true,
      }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const nameOf = (value) => {
    if (!value) return '';
    const [type, id] = value.split(':');
    const list = type === 'nodebot' ? bots.nodebots : bots.custom;
    return list.find((b) => String(b.id) === id)?.name || 'Unavailable chatbot';
  };
  return { ...bots, nameOf };
};

export const ChatbotSelect = ({ bots, value, onChange, label }) => {
  // A chatbot saved earlier but now off or deleted still shows, so the user sees why it isn't listed.
  const known = !value || bots.nameOf(value) !== 'Unavailable chatbot';
  return (
    <TextField select fullWidth size="small" label={label} value={value || ''} onChange={(e) => onChange(e.target.value)}>
      <MenuItem value="">No chatbot (normal replies)</MenuItem>
      {!known && <MenuItem value={value} disabled>Unavailable chatbot (switched off or deleted)</MenuItem>}
      <ListSubheader>Workflows</ListSubheader>
      {bots.nodebots.length === 0 && <MenuItem disabled>{bots.loaded ? 'No active workflows' : 'Loading…'}</MenuItem>}
      {bots.nodebots.map((b) => <MenuItem key={`n${b.id}`} value={`nodebot:${b.id}`}>{b.name || `Workflow ${b.id}`}</MenuItem>)}
      <ListSubheader>Custom chatbots</ListSubheader>
      {bots.custom.length === 0 && <MenuItem disabled>{bots.loaded ? 'No active custom chatbots' : 'Loading…'}</MenuItem>}
      {bots.custom.map((b) => <MenuItem key={`c${b.id}`} value={`custom_chatbot:${b.id}`}>{b.name}</MenuItem>)}
    </TextField>
  );
};

export const AutofillSection = ({ form, onChange, onAdChange }) => {
  const bots = useChatbots();
  return (
    <Stack spacing={3}>
      <Stack spacing={2}>
        <Typography variant="body2" fontWeight={700}>Autofill message</Typography>
        <Note>The message already typed in the customer's WhatsApp when they tap your ad.</Note>
        <AppliesTo label="Autofill applies to" mode={form.autofillMode} onChange={(autofillMode) => onChange({ autofillMode })} />
        {form.autofillMode === 'all'
          ? autofillField(form.autofillAll, (autofillAll) => onChange({ autofillAll }), 'Autofill message')
          : form.ads.map((ad, i) => (
            <Box key={ad.key}>{autofillField(ad.autofill, (autofill) => onAdChange(ad.key, { autofill }), `Ad ${i + 1} · ${ad.name}`)}</Box>
          ))}
      </Stack>

      <Stack spacing={2}>
        <Typography variant="body2" fontWeight={700}>Chatbot <Box component="span" sx={{ fontWeight: 400 }}>(optional)</Box></Typography>
        <Note>Link a workflow or custom chatbot to the ad.</Note>
        <AppliesTo label="Chatbot applies to" mode={form.chatbotMode} onChange={(chatbotMode) => onChange({ chatbotMode })} />
        {form.chatbotMode === 'all'
          ? <ChatbotSelect bots={bots} value={form.chatbotAll} onChange={(chatbotAll) => onChange({ chatbotAll })} label="Chatbot for all ads" />
          : form.ads.map((ad, i) => (
            <ChatbotSelect key={ad.key} bots={bots} value={ad.chatbot} onChange={(chatbot) => onAdChange(ad.key, { chatbot })}
              label={`Ad ${i + 1} · ${ad.name}`} />
          ))}
      </Stack>
    </Stack>
  );
};
